import React from 'react';
import { Modal } from 'antd';
import { motion } from 'framer-motion';
import { FcMoneyTransfer, FcAddImage, FcSalesPerformance, FcPaid } from 'react-icons/fc';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

interface HowItWorksModalProps {
    isOpen: boolean;
    onClose: () => void;
}

const steps = [
    {
        icon: <FcAddImage className="w-8 h-8" />,
        title: "Créez votre annonce",
        description: "Publiez vos articles en quelques clics avec photos et descriptions détaillées"
    },
    {
        icon: <FcSalesPerformance className="w-8 h-8" />,
        title: "Gérez vos ventes",
        description: "Suivez vos ventes, répondez aux messages et négociez avec les acheteurs"
    },
    {
        icon: <FcPaid className="w-8 h-8" />,
        title: "Gagnez de l'argent",
        description: "Recevez vos paiements de manière sécurisée et rapide"
    }
];

const HowItWorksModal: React.FC<HowItWorksModalProps> = ({ isOpen, onClose }) => {
    const { t } = useTranslation();

    return (
        <Modal
            open={isOpen}
            onCancel={onClose}
            footer={null}
            width={800}
            className="how-it-works-modal"
            centered
        >
            <div className="p-6">
                {/* Header */}
                <div className="text-center mb-8">
                    <motion.div
                        initial={{ scale: 0 }}
                        animate={{ scale: 1 }}
                        transition={{ type: "spring", stiffness: 200, damping: 20 }}
                        className="w-16 h-16 bg-primary-tertiary rounded-full flex items-center justify-center mx-auto mb-4"
                    >
                        <FcMoneyTransfer className="w-8 h-8" />
                    </motion.div>
                    <h2 className="text-2xl font-bold text-content-default mb-2">
                        {t('howItWorks.title', 'Comment ça marche ?')}
                    </h2>
                    <p className="text-content-tertiary">
                        {t('howItWorks.subtitle', 'Découvrez comment gagner de l\'argent en vendant sur Falaa')}
                    </p>
                </div>

                {/* Steps */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    {steps.map((step, index) => (
                        <motion.div
                            key={step.title}
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: index * 0.2 }}
                            className="bg-surface-secondary rounded-xl p-6 hover:shadow-lg transition-shadow"
                        >
                            <div className="flex flex-col items-center text-center">
                                <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center mb-4 shadow-sm">
                                    {step.icon}
                                </div>
                                <h3 className="text-lg font-semibold text-content-default mb-2">
                                    {step.title}
                                </h3>
                                <p className="text-sm text-content-tertiary">
                                    {step.description}
                                </p>
                            </div>
                        </motion.div>
                    ))}
                </div>

                {/* Call to Action */}
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.6 }}
                    className="mt-8 text-center"
                >
                    <Link
                        to="/sell"
                        onClick={onClose}
                        className="bg-primary-default text-white px-6 py-2 rounded-lg hover:bg-primary-secondary transition-colors inline-block"
                    >
                        {t('howItWorks.startButton', 'Commencer à vendre')}
                    </Link>
                </motion.div>
            </div>

            <style>
                {`
                    .how-it-works-modal .ant-modal-content {
                        border-radius: 1rem;
                        overflow: hidden;
                    }
                    
                    .how-it-works-modal .ant-modal-close {
                        top: 1rem;
                        right: 1rem;
                    }

                    .how-it-works-modal .ant-modal-close:hover {
                        background-color: var(--surface-tertiary);
                        border-radius: 50%;
                    }
                `}
            </style>
        </Modal>
    );
};

export default HowItWorksModal; 